import React, { useRef, useContext } from 'react';

import { ReactComponent as Calender } from '../../assets/icons/calender.svg';
import { ReactComponent as CalenderLight } from '../../assets/icons/dark-calender.svg';
import { ReactComponent as CalenderDark } from '../../assets/icons/iconsDark/calender-field-icon.svg';
import ThemeContext from '../../contexts/theme-context/ThemeContext';

interface CustomDateRangeProperties {
  inputLabel?: string;
  value1: string | number;
  value2: string | number;
  name1: string;
  name2: string;
  icon?: boolean;
  onChangeValue: (name: string, value: string | number) => void;
  valueRequired?: boolean;
  readOnly?: boolean;
  placeholder1?: string;
  placeholder2?: string;
  error?: boolean;
  errorMessage?: string;
}

/**
 * Icon click function
 */
const handleInputClick = (reference: React.RefObject<HTMLInputElement>) => {
  reference.current?.showPicker();
};

/**
 * Custom Date Range function component
 */
function CustomDateRange({
  inputLabel,
  name1,
  name2,
  icon,
  value1,
  value2,
  onChangeValue,
  valueRequired,
  placeholder1,
  error,
  errorMessage,
  placeholder2,
  readOnly,
}: CustomDateRangeProperties) {
  const fromDateInputReference = useRef(null);
  const toDateInputReference = useRef(null);
  const { theme } = useContext(ThemeContext);
  /**
   * Date Range field handle function
   */
  const handleValueChange = (name: string, changeValue: string | number) =>
    onChangeValue(name, changeValue);
  /**
   * getCalendarIcon
   */
  const getCalendarIcon = () => {
    if (icon) {
      return <CalenderLight />;
    }
    return theme === 'light' ? <Calender /> : <CalenderDark />;
  };

  return (
    <div className="custom-input-date-range-wrap">
      {inputLabel && (
        <p className="input-label">
          {inputLabel}
          {valueRequired && <span>*</span>}
        </p>
      )}
      <div className="custom-date-input">
        <div className="custom-input-container">
          {value1 === '' && <span className="custom-placeholder">{placeholder1}</span>}
          <input
            type="date"
            placeholder={placeholder1}
            value={value1}
            onChange={(event) => handleValueChange(name1, event.target.value)}
            required={valueRequired}
            readOnly={readOnly}
            ref={fromDateInputReference}
            onClick={() => handleInputClick(fromDateInputReference)}
            style={{ opacity: value1 ? 1 : 0 }}
          />
          <span role="presentation" onClick={() => handleInputClick(fromDateInputReference)}>
            {getCalendarIcon()}
          </span>
        </div>
        <div className="custom-input-container">
          {value2 === '' && <span className="custom-placeholder">{placeholder2}</span>}
          <input
            type="date"
            placeholder={placeholder2}
            value={value2}
            onChange={(event) => handleValueChange(name2, event.target.value)}
            required={valueRequired}
            readOnly={readOnly}
            ref={toDateInputReference}
            onClick={() => handleInputClick(toDateInputReference)}
            style={{ opacity: value2 ? 1 : 0 }}
          />
          <span role="presentation" onClick={() => handleInputClick(toDateInputReference)}>
            {getCalendarIcon()}
          </span>
        </div>
      </div>
      {error && <p className="custom-input-error-message">{errorMessage}</p>}
    </div>
  );
}

CustomDateRange.defaultProps = {
  inputLabel: '',
  valueRequired: false,
  readOnly: false,
  icon: false,
  error: false,
  placeholder1: "From",
  placeholder2: "To",
  errorMessage: ""
};

export default CustomDateRange;
